import { ActionIcon, Button, Paper, Progress, Text } from '@mantine/core';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { calculateProgress } from '../utils/calculate-progress';

import classes from './fields-navbar.module.css';

interface FieldsNavbarProps {
  currentFieldIndex: number;
  setCurrentFieldIndex: React.Dispatch<React.SetStateAction<number>>;
  fieldsLength: number;
  hasMultipleFields: boolean;
  isCurrentFieldComplete: boolean;
  isFormComplete: boolean;
  progressValue: number;
  setProgressValue: React.Dispatch<React.SetStateAction<number>>;

  isPending: boolean;
  handleConfirm: () => void;
  onBackToChoice: () => void;
}

export const FieldsNavbar: React.FC<FieldsNavbarProps> = ({
  currentFieldIndex,
  setCurrentFieldIndex,
  progressValue,
  setProgressValue,
  isPending,
  handleConfirm,
  fieldsLength,
  hasMultipleFields,
  isCurrentFieldComplete,
  isFormComplete,
  onBackToChoice,
}) => {
  const { t } = useTranslation();

  const handlePrev = () => {
    if (currentFieldIndex > 0) {
      setCurrentFieldIndex((prev) => prev - 1);
      setProgressValue(calculateProgress(currentFieldIndex - 1, fieldsLength));
    }
  };

  const handleNext = () => {
    if (!isCurrentFieldComplete) {
      return;
    }

    setCurrentFieldIndex((prev) => prev + 1);
    setProgressValue(calculateProgress(currentFieldIndex + 1, fieldsLength));
  };

  const currentStep = currentFieldIndex + 1;
  const isLastField = currentFieldIndex === fieldsLength - 1;
  const showNextControl = hasMultipleFields && !isLastField;

  return (
    <Paper className={classes.root} withBorder>
      <button
        className={classes.backLink}
        onClick={onBackToChoice}
        type="button"
      >
        <IconArrowLeft aria-hidden size={16} stroke={1.5} />
        {t('common.back')}
      </button>

      <div className={classes.navRow}>
        <ActionIcon
          aria-label={t('farmer-validation.field-prev')}
          disabled={!hasMultipleFields || currentFieldIndex === 0}
          onClick={handlePrev}
          radius="xl"
          size="xl"
          variant="default"
        >
          <IconArrowLeft stroke={1.5} />
        </ActionIcon>

        <div className={classes.counter}>
          <span className={classes.counterLabel}>
            {t('farmer-validation.field-counter-label')}
          </span>
          <span className={classes.counterValue}>
            {t('farmer-validation.field-step-count', {
              current: currentStep,
              total: fieldsLength,
            })}
          </span>
        </div>

        {showNextControl ? (
          <ActionIcon
            aria-label={t('common.prochaine-parcelle')}
            disabled={!isCurrentFieldComplete}
            onClick={handleNext}
            radius="xl"
            size="xl"
            variant="default"
          >
            <IconArrowRight stroke={1.5} />
          </ActionIcon>
        ) : (
          <span aria-hidden className={classes.navSpacer} />
        )}
      </div>

      {!hasMultipleFields || isLastField ? (
        <div className={classes.validateRow}>
          <Button
            disabled={!isFormComplete}
            fullWidth
            loading={isPending}
            onClick={handleConfirm}
            size="md"
          >
            {t('common.valider-les-informations')}
          </Button>
        </div>
      ) : null}

      <Progress
        className={classes.progress}
        color={isCurrentFieldComplete ? 'pf-green' : 'yellow'}
        size="sm"
        transitionDuration={200}
        value={progressValue}
      />
    </Paper>
  );
};
